import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Eye, Mail, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Segmented, SettingsCard, ToggleRow } from './primitives';
import { buildEmbedSnippet, embedUrl, type EmbedOptions } from '../draft';

type Platform = 'html' | 'wordpress' | 'wix' | 'squarespace';

interface EmbedCardProps {
  subdomain: string;
  orgName: string;
  allowedOrigins: string[];
  onAllowedOrigins: (origins: string[]) => void;
}

// Normalizes like BookingPageSettings.clean_origins so chips show what's saved.
function toOrigin(entry: string): string | null {
  const raw = entry.trim();
  if (!raw) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$|^localhost$/i.test(url.hostname)) return null;
    return `${url.protocol}//${url.host}`.toLowerCase();
  } catch {
    return null;
  }
}

export function EmbedCard({ subdomain, orgName, allowedOrigins, onAllowedOrigins }: EmbedCardProps) {
  const { t } = useTranslation('adminBookingPage');
  const { toast } = useToast();
  const [options, setOptions] = useState<EmbedOptions>({ header: false, photos: true, sidebar: true, autoHeight: true });
  const [platform, setPlatform] = useState<Platform>('html');
  const [site, setSite] = useState('');
  const snippet = buildEmbedSnippet(subdomain, orgName, options);
  const set = (patch: Partial<EmbedOptions>) => setOptions((o) => ({ ...o, ...patch }));

  const copy = async () => {
    await navigator.clipboard?.writeText(snippet);
    toast({ title: t('embed.copied') });
  };

  const addSite = () => {
    const origin = toOrigin(site);
    if (!origin) return;
    if (!allowedOrigins.includes(origin)) onAllowedOrigins([...allowedOrigins, origin]);
    setSite('');
  };

  const mailto = `mailto:?subject=${encodeURIComponent(t('embed.emailSubject'))}&body=${encodeURIComponent(`${t(`embed.instructions.${platform}`)}\n\n${snippet}`)}`;

  return (
    <SettingsCard title={t('embed.title')} description={t('embed.description')} testId="embed-card">
      <div className="grid rounded-xl border border-line px-4 sm:grid-cols-2 sm:gap-x-7">
        <ToggleRow id="embed-header" first label={t('embed.header')} hint={t('embed.headerHint')} checked={options.header} onChange={(header) => set({ header })} />
        <div className="sm:border-t-0 [&>div]:sm:border-t-0"><ToggleRow id="embed-photos" label={t('embed.photos')} hint={t('embed.photosHint')} checked={options.photos} onChange={(photos) => set({ photos })} /></div>
        <ToggleRow id="embed-sidebar" label={t('embed.sidebar')} hint={t('embed.sidebarHint')} checked={options.sidebar} onChange={(sidebar) => set({ sidebar })} />
        <ToggleRow id="embed-auto-height" label={t('embed.autoHeight')} hint={t('embed.autoHeightHint')} checked={options.autoHeight} onChange={(autoHeight) => set({ autoHeight })} />
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="max-w-full overflow-x-auto">
            <Segmented
              label={t('embed.title')}
              value={platform}
              onChange={(v) => setPlatform(v as Platform)}
              options={(['html', 'wordpress', 'wix', 'squarespace'] as Platform[]).map((p) => ({ value: p, label: t(`embed.platforms.${p}`) }))}
            />
          </div>
          <div className="hidden gap-2 sm:flex">
            <Button asChild variant="outline" className="h-10">
              <a href={embedUrl(subdomain, options)} target="_blank" rel="noreferrer"><Eye className="h-4 w-4" />{t('embed.preview')}</a>
            </Button>
            <Button className="h-10" onClick={copy}><Copy className="h-4 w-4" />{t('embed.copy')}</Button>
          </div>
        </div>
        <pre className="whitespace-pre-wrap break-all rounded-lg border border-line bg-surface-2 px-4 py-3.5 font-mono text-[12px] leading-relaxed text-ink sm:text-[13px]" data-testid="embed-snippet">{snippet}</pre>
        <p className="text-body-sm text-soft" data-testid="embed-instructions">{t(`embed.instructions.${platform}`)}</p>
        <div className="grid grid-cols-2 gap-2 sm:hidden">
          <Button asChild variant="outline" className="h-12"><a href={mailto}><Mail className="h-4 w-4" />{t('embed.email')}</a></Button>
          <Button className="h-12" onClick={copy}><Copy className="h-4 w-4" />{t('embed.copy')}</Button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="allowed-site" className="text-label font-semibold text-ink">{t('embed.allowedTitle')}</label>
        <div className="flex min-h-[44px] flex-wrap items-center gap-1.5 rounded-md border border-line bg-surface p-1.5" data-testid="allowed-origins">
          {allowedOrigins.map((origin) => (
            <span key={origin} className="inline-flex h-8 items-center gap-1 rounded-sm bg-surface-2 pl-2.5 text-[13px] font-medium text-ink">
              {origin.replace(/^https:\/\//, '')}
              <button type="button" aria-label={t('embed.remove', { site: origin })} onClick={() => onAllowedOrigins(allowedOrigins.filter((o) => o !== origin))} className="inline-flex h-8 w-8 items-center justify-center text-soft hover:text-ink">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <Input
            id="allowed-site"
            value={site}
            placeholder={t('embed.allowedPlaceholder')}
            onChange={(e) => setSite(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSite(); } }}
            className="h-8 min-w-[180px] flex-1 border-0 px-1.5 shadow-none focus-visible:ring-0"
          />
          <Button type="button" variant="ghost" size="sm" onClick={addSite} disabled={!toOrigin(site)}><Plus className="h-3.5 w-3.5" />{t('embed.add')}</Button>
        </div>
        <span className="text-body-xs text-soft">{t('embed.allowedHint')}</span>
      </div>
    </SettingsCard>
  );
}
